<script setup lang="ts">
/**
 * Bebawa App Root Component
 *
 * This file contains app-specific initialization logic including:
 * - Feature store initialization
 * - Initial data fetching on login
 * - Dev tools configuration
 */

import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import ProgressSpinner from 'primevue/progressspinner';

// Feature stores
import { useAuth0 } from '@auth0/auth0-vue';
import { useHttpSecurityHandlers } from '@peanuds/app-shell/composables/useHttpSecurityHandlers';
import {
  InactivityTimer,
  PendingLivenessDialog,
  SmallScreenDialog,
  useAccessControlStore,
  useActiveIdentity,
  useAuthIdentityTabSync,
  useAuthStore,
  useAuthSessionStore,
  useAuthTokenRefreshInterval,
  useEnsureHasAccessToast,
  useHubspotChatAutoClose,
  useOnboardedBootstrap,
  useSocketConnection,
  useUserContextStore,
} from '@peanuds/app-shell';
import { useNotificationsInitialization } from '@peanuds/modules';
import { UiActionToast } from '@peanuds/ui';
import { useBeneficiariesStore, useUserAccountsStore } from '@peanuds/shared-state';
import { jam } from '@jam.dev/sdk';

// Initialize socket lifecycle (connect on JWT, disconnect on logout)
useSocketConnection();
useEnsureHasAccessToast();
useHttpSecurityHandlers();
useHubspotChatAutoClose();
useAuthTokenRefreshInterval();

// Auth0 context
const auth0 = useAuth0();
const activeIdentity = useActiveIdentity();
const authSessionStore = useAuthSessionStore();
const { isLoggedIn, idToken, organizationId } = storeToRefs(authSessionStore);
useAuthIdentityTabSync(activeIdentity.identityKey, organizationId);

// Initialize stores
const userAccountsStore = useUserAccountsStore();
const { ensureAccountsLoaded, ensureValidActiveAccount } = userAccountsStore;

const authStore = useAuthStore();
const userContextStore = useUserContextStore();
const { isLoading: isLoadingUserContexts, isLoaded: hasLoadedUserContexts } =
  storeToRefs(userContextStore);
const routeOwner = computed(() => `${activeIdentity.identityKey.value}:${organizationId.value}`);

const beneficiariesStore = useBeneficiariesStore();
const { fetchBeneficiaries } = beneficiariesStore;

const accessControlStore = useAccessControlStore();
const { isOnboarded } = storeToRefs(accessControlStore);
const { ensureAccessResolved } = accessControlStore;

// Initial data fetching logic
const fetchInitialData = async () => {
  try {
    await ensureAccessResolved();

    if (!isOnboarded.value) return;

    await ensureAccountsLoaded();
    ensureValidActiveAccount();

    // Fetch beneficiary list only. Bank details are loaded when a beneficiary is inspected.
    await fetchBeneficiaries();
  } catch (error) {
    console.error('Failed to fetch initial data:', error);
  }
};

useOnboardedBootstrap(() => fetchInitialData(), {
  dedupeKey: () => activeIdentity.identityKey.value,
});

// Notifications initialization (handles subscriber identification + Novu client setup)
useNotificationsInitialization({ isLoggedIn, idToken, organizationId, auth0 });

// Captured when someone creates a Jam.
jam.metadata(() => {
  const emailRequirement = accessControlStore.getRequirement('email');
  const phoneRequirement = accessControlStore.getRequirement('phone');
  const kycRequirement = accessControlStore.getRequirement('kyc');
  const kybRequirement = accessControlStore.getRequirement('kyb');

  return {
    user: {
      id: activeIdentity.userId.value,
      organizationId: authSessionStore.organizationId,
    },
    onboarding: {
      accessState: accessControlStore.accessState,
      isOnboarded: accessControlStore.isOnboarded,
      emailStatus: emailRequirement?.status ?? null,
      phoneStatus: phoneRequirement?.status ?? null,
      kycStatus: kycRequirement?.status ?? null,
      kybStatus: kybRequirement?.status ?? null,
    },
  };
});
</script>

<template>
  <main v-if="isLoadingUserContexts" class="bebawa-app__loader" aria-busy="true" aria-live="polite">
    <ProgressSpinner aria-label="Loading organizations" />
  </main>
  <div
    v-if="!isLoadingUserContexts || hasLoadedUserContexts"
    v-show="!isLoadingUserContexts"
    :inert="isLoadingUserContexts || undefined"
    class="bebawa-app__route"
  >
    <RouterView :key="routeOwner" />
  </div>
  <Toast />
  <UiActionToast />
  <SmallScreenDialog />
  <PendingLivenessDialog />
  <InactivityTimer :auth-store="authStore" />
</template>

<style lang="scss" scoped>
.bebawa-app {
  &__route {
    height: 100%;
  }

  &__loader {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
  }
}
</style>
